import { SignalingClient } from './signaling.js';
import { IceConfigError, WebRTCManager } from './webrtc.js';
import { API_ORIGIN } from './config.js';
import { getLiveStatus } from './liveHours.js';

const STORAGE_KEY = 'active_session';
const PREV_SESSION_KEY = 'previous_session';
const AGE_KEY = 'pulse_talk_18';
const CLIENT_KEY = 'pulse_talk_client';
const PRESENCE_POLL_MS = 10_000;
const REPORT_NOTE_MAX = 200;
const DEFAULT_SEARCH_TIMEOUT_MS = Number(import.meta.env?.VITE_SEARCH_TIMEOUT_MS) || 60_000;

const REPORT_REASONS = [
  { id: 'sexual', label: 'Sexual / unwanted' },
  { id: 'harassment', label: 'Harassment' },
  { id: 'underage', label: 'Sounds underage' },
  { id: 'spam', label: 'Spam / bot' },
  { id: 'other', label: 'Other' },
];

async function fetchPresenceCount() {
  if (typeof fetch !== 'function') return null;
  const response = await fetch(`${API_ORIGIN}/api/presence`, { cache: 'no-store' });
  if (!response.ok) return null;
  const data = await response.json();
  const count = Number(data?.count);
  return Number.isFinite(count) ? count : null;
}

function isIceConfigError(error) {
  return error instanceof IceConfigError || error?.name === 'IceConfigError';
}

export class App {
  constructor({
    signaling = new SignalingClient(),
    webrtc = new WebRTCManager(),
    root = document.getElementById('app'),
    storage = window.localStorage,
    idFactory = () => crypto.randomUUID(),
    clock = { now: () => Date.now() },
    timers = window,
    presence = { getCount: fetchPresenceCount },
    searchTimeoutMs = DEFAULT_SEARCH_TIMEOUT_MS,
  } = {}) {
    this.signaling = signaling;
    this.webrtc = webrtc;
    this.root = root;
    this.storage = storage;
    this.idFactory = idFactory;
    this.clock = clock;
    this.timers = timers;
    this.presence = presence;
    this.searchTimeoutMs = searchTimeoutMs;

    this.sessionId = null;
    this.peerSessionId = null;
    this.state = 'IDLE';
    this.screen = 'home';
    this.matchPhase = 'searching';
    this.isMuted = false;
    this.isSpeaking = false;
    this.isDark = false;
    this.activeUsers = null;
    this.presenceReady = false;
    this.presenceIncludesSelf = false;
    this.presenceEpoch = 0;
    this.dropOneSelfFromPresence = false;
    this.callStartedAt = null;
    this.callSeconds = 0;
    this.skipArmed = false;
    this.errorMessage = '';
    this.leaving = false;
    this.mediaLive = false;
    this.lastIsDesktop = this.isDesktop();
    this.peerReconnectingAt = null;
    this.reconnectGracePeriodMs = 800;
    this.reconnectFallbackTimer = null;
    this.matchEnterTimer = null;
    this.searchTimer = null;
    this.searchGeneration = 0;
    this.presenceTimer = null;
    this.skipResetTimer = null;
    this.speakingTimer = null;
    this.callTimer = null;
    this.errorTimer = null;
    this.signalingReconnectTimer = null;
    this.reconnectToastTimer = null;
    this.showAgeGate = false;
    this.reportOpen = false;
    this.reportReason = '';
    this.reportNote = '';
    this.sessionStatus = 'idle';
    this.currentMatch = null;
    this.shell = null;
    this.eventsBound = false;

    this.initListeners();
    this.render();
    this.startPresencePolling();
  }

  initListeners() {
    this.signaling.on('MATCH_FOUND', (message) => this.handleMatchFound(message));
    this.signaling.on('OFFER', (message) => this.handleOffer(message));
    this.signaling.on('ANSWER', (message) => this.handleAnswer(message));
    this.signaling.on('ICE_CANDIDATE', (message) => this.handleIceCandidate(message));
    this.signaling.on('PEER_LEFT', () => this.handlePeerLeft());
    this.signaling.on('PEER_RECONNECTING', () => this.handlePeerReconnecting());
    this.signaling.on('SESSION_RESUME', () => this.handleSessionResume());
    this.signaling.on('TIMEOUT', () => this.handleTimeout());
    this.signaling.on('ACTIVE_USERS', (message) => this.applySocketPresence(message.count));
    this.signaling.on('ERROR', (message) => this.showError(message.message));
    this.signaling.on('close', () => this.handleDisconnect());

    this.webrtc.onIceCandidate = (candidate) => this.signaling.sendIceCandidate(candidate);
    this.webrtc.onRemoteStream = (stream) => {
      this.playRemoteAudio(stream);
      this.markCallLive();
    };
    this.webrtc.onConnectionState = (state) => this.handleConnectionState(state);
    this.webrtc.onIceRestart = (offer) => this.signaling.sendOffer(offer);

    window.addEventListener('beforeunload', () => this.saveSessionForReconnect());
    window.addEventListener('resize', () => {
      const desktop = this.isDesktop();
      if (desktop === this.lastIsDesktop) return;
      this.lastIsDesktop = desktop;
      this.render();
    });
  }

  // Patch the existing tree. Replacing #app innerHTML remounts the shell/orb and flashes.
  render() {
    document.documentElement.dataset.theme = this.isDark ? 'dark' : 'light';
    if (!this.shell || !this.root.contains(this.shell)) {
      this.mountShell();
    }
    this.syncView();
  }

  el(name) {
    return this.shell?.querySelector(`[data-el="${name}"]`) ?? null;
  }

  mountShell() {
    this.root.innerHTML = `
      <div class="app-shell" data-el="shell" data-session="idle">
        <nav class="top-nav desktop-only" data-el="desktop-nav">
          ${this.renderBrand('brand-desktop')}
          <div class="nav-count" data-el="presence" data-presence></div>
          <button class="theme-toggle" data-action="toggle-theme" data-el="theme-desktop">Dark</button>
        </nav>
        <div class="timer-chip hidden" data-el="timer-desktop">00:00</div>
        <header class="mobile-header" data-el="mobile-header">
          <div data-el="mobile-start"></div>
          ${this.renderBrand('brand-mobile')}
          <div data-el="mobile-end">
            <button class="icon-button" data-action="toggle-theme" data-el="theme-mobile" aria-label="Toggle color mode">⚙</button>
          </div>
        </header>
        <div class="hidden" data-el="stash"></div>
        <section class="screen home" data-el="stage">
          <div class="timer-chip hidden" data-el="timer-mobile">00:00</div>
          <div class="center-stack" data-el="main">
            <h2 class="heading" data-el="heading"></h2>
            <p class="subtitle" data-el="subtitle"></p>
            <p class="hint" data-el="idle-link"><a class="text-link" href="/no-camera.html">Practice speaking — no camera</a></p>
            <p class="hint" data-el="live-hours"></p>
            <p class="hint hidden" data-el="mobile-presence"></p>
            ${this.renderOrb({ speaking: false, matching: false })}
            <div class="found-pill hidden" data-el="found-pill"></div>
            <h2 class="match-title hidden" data-el="match-title"></h2>
            <p class="eyebrow hidden" data-el="search-hint"></p>
            <div class="wave-bars hidden" data-el="wave-bars">
              ${Array.from({ length: 7 }, () => '<span></span>').join('')}
            </div>
            <div class="partner hidden" data-el="partner">
              <div class="connection-state">${this.renderDot()}<span data-el="connection-label" data-connection-label></span></div>
            </div>
            <button class="cta" data-action="join" data-el="join">Say Hello 👋</button>
            <button class="cta hidden" data-action="retry-voice" data-el="retry">Retry</button>
            <button class="ghost hidden" data-action="cancel" data-el="cancel">Cancel</button>
            <button class="text-link hidden" data-action="home" data-el="back-home">Back home</button>
          </div>
          <div class="controls-area hidden" data-el="controls-area">
            <div class="tooltip hidden" data-el="skip-tooltip">Tap again to find new match</div>
            <div class="controls" data-el="controls">
              <button class="control" data-action="mute" data-el="mute">
                <span class="control-icon" data-el="mute-icon">🎙</span>
                <span data-mute-label>Mute</span>
              </button>
              <button class="control" data-action="new-match" data-el="skip">
                <span class="control-icon">⏭</span>
                <span>New Match</span>
              </button>
              <button class="control report" data-action="open-report">
                <span class="control-icon">⚑</span>
                <span>Report</span>
              </button>
              <button class="control leave" data-action="leave">
                <span class="control-icon">📵</span>
                <span>Leave</span>
              </button>
            </div>
          </div>
        </section>
      </div>
    `;
    this.shell = this.root.querySelector('.app-shell');
    this.bindEvents();
  }

  syncSessionStatus() {
    if (this.reportOpen) this.sessionStatus = 'report';
    else if (this.screen === 'active') this.sessionStatus = 'connected';
    else if (this.screen === 'matching' || this.screen === 'empty') this.sessionStatus = 'searching';
    else this.sessionStatus = 'idle';
  }

  syncView() {
    this.syncSessionStatus();
    document.documentElement.dataset.theme = this.isDark ? 'dark' : 'light';
    if (!this.shell) return;

    this.shell.dataset.session = this.sessionStatus;

    const desktop = this.isDesktop();
    const home = this.screen === 'home';
    const matching = this.screen === 'matching';
    const found = matching && this.matchPhase === 'found';
    const searching = matching && !found;
    const empty = this.screen === 'empty';
    const voice = this.screen === 'voice-setup';
    const active = this.screen === 'active';
    const speaking = (active && this.isSpeaking && !this.isMuted) || found;

    const stage = this.el('stage');
    if (stage) {
      stage.classList.toggle('home', home);
      stage.classList.toggle('active-screen', active);
      stage.classList.toggle('empty-screen', empty);
    }

    const main = this.el('main');
    if (main) {
      main.classList.toggle('active-core', active);
      main.classList.toggle('center-stack', !active);
    }

    this.syncChrome({ desktop, active, matching });
    this.syncPresence();

    const orb = this.el('orb');
    if (orb) {
      orb.classList.toggle('speaking', speaking);
      orb.querySelectorAll('.match-ring').forEach((ring) => {
        ring.classList.toggle('hidden', !searching);
      });
    }

    const heading = this.el('heading');
    if (heading) {
      heading.classList.toggle('hidden', !(home || empty || voice));
      if (home) heading.innerHTML = `${this.getGreeting()}<br>Ready to chat?`;
      else if (empty) heading.textContent = "Nobody's here yet.";
      else if (voice) heading.textContent = "Can't set up voice — retry";
      else heading.textContent = '';
    }

    const subtitle = this.el('subtitle');
    if (subtitle) {
      subtitle.classList.toggle('hidden', !(home || empty || voice));
      if (home) subtitle.textContent = 'Connect with a random stranger — voice first.';
      else if (empty) {
        subtitle.textContent = this.getPresence().others > 0
          ? "Someone's around now. Try again to find them."
          : 'The room is empty — no one is waiting to talk.';
      } else if (voice) {
        subtitle.textContent = "Voice routing didn't load, so the call can't start. Your microphone stays ready.";
      } else {
        subtitle.textContent = '';
      }
    }

    this.toggleHidden(this.el('idle-link'), !home);

    const liveHours = this.el('live-hours');
    if (liveHours) {
      liveHours.classList.toggle('hidden', !(home || empty));
      liveHours.textContent = home || empty ? this.liveHoursLine() : '';
    }

    const mobilePresence = this.el('mobile-presence');
    if (mobilePresence) {
      const showHint = home && !desktop && this.presenceReady;
      mobilePresence.classList.toggle('hidden', !showHint);
      mobilePresence.textContent = showHint ? this.getPresence().text : '';
    }

    const foundPill = this.el('found-pill');
    if (foundPill) {
      foundPill.classList.toggle('hidden', !found);
      foundPill.textContent = found ? 'Match found! Connecting…' : '';
    }

    const matchTitle = this.el('match-title');
    if (matchTitle) {
      matchTitle.classList.toggle('hidden', !searching);
      if (searching && !matchTitle.querySelector('#dots')) {
        matchTitle.innerHTML = `Finding someone<span id="dots">${this.matchDots()}</span>`;
      } else if (!searching) {
        matchTitle.innerHTML = '';
      }
    }

    const searchHint = this.el('search-hint');
    if (searchHint) {
      searchHint.classList.toggle('hidden', !searching);
      searchHint.textContent = searching ? this.searchHintText() : '';
    }

    const waveBars = this.el('wave-bars');
    if (waveBars) {
      waveBars.classList.toggle('hidden', !active);
      waveBars.classList.toggle('active', speaking && active);
    }

    const partner = this.el('partner');
    const connectionLabel = this.el('connection-label');
    if (partner) partner.classList.toggle('hidden', !active);
    if (connectionLabel) connectionLabel.textContent = active ? 'Active voice connection' : '';

    const join = this.el('join');
    if (join) {
      join.classList.toggle('hidden', !(home || empty));
      if (home) join.textContent = 'Say Hello 👋';
      else if (empty) join.textContent = 'Try again';
    }

    this.toggleHidden(this.el('retry'), !voice);
    this.toggleHidden(this.el('cancel'), !(matching || voice));
    this.toggleHidden(this.el('back-home'), !empty);
    this.toggleHidden(this.el('timer-mobile'), !(active && !desktop));
    this.toggleHidden(this.el('controls-area'), !active);
    this.toggleHidden(this.el('skip-tooltip'), !(active && this.skipArmed));

    this.root.querySelectorAll('.timer-chip').forEach((timer) => {
      timer.textContent = this.formatTime(this.callSeconds);
    });

    const mute = this.el('mute');
    if (mute) {
      mute.classList.toggle('muted', this.isMuted);
      const icon = this.el('mute-icon');
      const label = mute.querySelector('[data-mute-label]');
      if (icon) icon.textContent = this.isMuted ? '🔇' : '🎙';
      if (label) label.textContent = this.isMuted ? 'Unmute' : 'Mute';
    }

    this.el('skip')?.classList.toggle('skip-armed', Boolean(active && this.skipArmed));

    const themeDesktop = this.el('theme-desktop');
    if (themeDesktop) themeDesktop.textContent = this.isDark ? 'Light' : 'Dark';

    this.syncOverlays();
    this.syncErrorToast();
  }

  toggleHidden(el, hidden) {
    if (!el) return;
    el.classList.toggle('hidden', hidden);
  }

  setChildren(parent, nodes) {
    if (!parent) return;
    const next = nodes.filter(Boolean);
    const current = [...parent.children];
    if (current.length === next.length && current.every((node, index) => node === next[index])) {
      return;
    }
    parent.replaceChildren(...next);
  }

  stashNode(node) {
    const stash = this.el('stash');
    if (!node || !stash || node.parentElement === stash) return;
    stash.append(node);
  }

  syncChrome({ desktop, active, matching }) {
    const desktopNav = this.el('desktop-nav');
    const brandDesktop = this.el('brand-desktop');
    const presence = this.el('presence');
    const themeDesktop = this.el('theme-desktop');
    const timerDesktop = this.el('timer-desktop');
    const themeMobile = this.el('theme-mobile');
    const mobileStart = this.el('mobile-start');
    const mobileEnd = this.el('mobile-end');

    this.toggleHidden(this.el('desktop-nav'), !desktop);
    this.toggleHidden(this.el('mobile-header'), desktop);

    if (presence) {
      presence.className = active ? 'user-count' : 'nav-count';
      presence.setAttribute('data-presence', '');
    }

    if (desktop) {
      if (active) {
        this.setChildren(desktopNav, [presence, brandDesktop, timerDesktop]);
        this.stashNode(themeDesktop);
        this.toggleHidden(timerDesktop, false);
      } else {
        this.setChildren(desktopNav, [brandDesktop, presence, themeDesktop]);
        this.stashNode(timerDesktop);
        this.toggleHidden(timerDesktop, true);
      }
      this.setChildren(mobileStart, []);
    } else if (active) {
      this.setChildren(mobileStart, [presence]);
      this.toggleHidden(presence, !this.presenceReady);
      this.stashNode(timerDesktop);
      this.stashNode(themeDesktop);
    } else {
      this.setChildren(mobileStart, []);
      this.stashNode(presence);
      this.stashNode(timerDesktop);
      this.stashNode(themeDesktop);
    }

    if (matching) this.stashNode(themeMobile);
    else this.setChildren(mobileEnd, [themeMobile]);
  }

  syncPresence() {
    const el = this.el('presence');
    if (!el) return;
    const hidden = el.classList.contains('hidden') || Boolean(el.closest('.hidden'));
    if (!this.presenceReady || hidden) {
      el.innerHTML = '';
      return;
    }
    const presence = this.getPresence();
    const dot = this.renderDot(presence.kind === 'crowd' ? 'dot' : 'dot quiet');
    el.innerHTML = `${dot}<span data-online-label>${this.escape(presence.text)}</span>`;
  }

  syncOverlays() {
    const age = this.shell?.querySelector('[data-age-gate]');
    if (this.showAgeGate && !age) {
      this.shell.insertAdjacentHTML('beforeend', this.renderAgeGate());
    } else if (!this.showAgeGate) {
      age?.remove();
    }

    const report = this.shell?.querySelector('[data-report-sheet]');
    if (this.reportOpen && !report) {
      this.shell.insertAdjacentHTML('beforeend', this.renderReportSheet());
    } else if (!this.reportOpen) {
      report?.remove();
    }
  }

  syncErrorToast() {
    if (this.errorMessage) {
      if (!this.root.querySelector('.error-toast')) this.showError(this.errorMessage);
      return;
    }
    this.root.querySelector('.error-toast')?.remove();
  }

  searchHintText() {
    const presence = this.getPresence();
    if (!this.presenceReady || presence.others <= 0) {
      return 'Waiting for someone to join';
    }
    const label = presence.others === 1 ? 'voice' : 'voices';
    return `Looking across ${presence.others} ${label}`;
  }

  renderAgeGate() {
    return `
      <div class="sheet-backdrop" data-action="decline-age" data-age-gate>
        <div class="sheet" role="dialog" aria-labelledby="age-title" data-stop>
          <h2 id="age-title" class="sheet-title">You must be 18 or older</h2>
          <p class="sheet-copy">Pulse Talk is anonymous voice chat with strangers. Confirm you are an adult to continue.</p>
          <button class="cta" data-action="confirm-age">I am 18 or older</button>
          <button class="ghost" data-action="decline-age">I'm under 18</button>
        </div>
      </div>
    `;
  }

  renderReportSheet() {
    const reasons = REPORT_REASONS.map((reason) => `
      <button
        type="button"
        class="reason-chip ${this.reportReason === reason.id ? 'selected' : ''}"
        data-action="pick-reason"
        data-reason="${reason.id}"
      >${reason.label}</button>
    `).join('');

    return `
      <div class="sheet-backdrop" data-action="close-report" data-report-sheet>
        <div class="sheet" role="dialog" aria-labelledby="report-title" data-stop>
          <h2 id="report-title" class="sheet-title">Report this person</h2>
          <p class="sheet-copy">You won't be matched with them again. They won't be told you reported them.</p>
          <div class="reason-grid">${reasons}</div>
          <label class="sheet-label" for="report-note">Anything we should know? (optional)</label>
          <textarea id="report-note" class="report-note" maxlength="${REPORT_NOTE_MAX}" placeholder="Keep it short." data-report-note>${this.escape(this.reportNote)}</textarea>
          <button class="cta" data-action="submit-report" ${this.reportReason ? '' : 'disabled'}>Submit report</button>
          <button class="ghost" data-action="close-report">Cancel</button>
        </div>
      </div>
    `;
  }

  renderBrand(slot = '') {
    const attr = slot ? ` data-el="${slot}"` : '';
    return `
      <h1 class="brand"${attr}>
        <span class="brand-mark" aria-hidden="true">
          <svg viewBox="0 0 32 32" fill="none">
            <circle class="brand-ring-outer" cx="16" cy="16" r="13.25" stroke="currentColor" stroke-width="1.2"/>
            <circle cx="16" cy="16" r="8.35" stroke="currentColor" stroke-width="1.35" opacity="0.58"/>
            <circle cx="16" cy="16" r="3.15" fill="currentColor"/>
          </svg>
        </span>
        <span class="brand-name">Pulse Talk</span>
      </h1>
    `;
  }

  renderOrb({ speaking, matching }) {
    return `
      <div class="orb-wrap ${speaking ? 'speaking' : ''}" data-el="orb" style="--orb-size:${this.isDesktop() ? 200 : 180}px">
        <div class="match-ring ${matching ? '' : 'hidden'}"></div>
        <div class="match-ring ${matching ? '' : 'hidden'}"></div>
        <div class="match-ring ${matching ? '' : 'hidden'}"></div>
        <div class="orb-glow"></div>
        <div class="orb" data-el="orb-sphere"></div>
      </div>
    `;
  }

  renderDot(className = 'dot') {
    return `<span class="${className}" aria-hidden="true"></span>`;
  }

  bindEvents() {
    if (this.eventsBound) return;
    this.eventsBound = true;
    this.root.addEventListener('click', (event) => this.onClick(event));
    this.root.addEventListener('input', (event) => this.onInput(event));
  }

  onClick(event) {
    const actionEl = event.target.closest?.('[data-action]');
    if (!actionEl || !this.root.contains(actionEl)) return;
    if (actionEl.classList.contains('sheet-backdrop') && event.target.closest('[data-stop]')) {
      return;
    }

    const action = actionEl.dataset.action;
    if (action === 'join') this.joinQueue();
    if (action === 'retry-voice') this.joinQueue();
    if (action === 'cancel') this.leave();
    if (action === 'leave') this.leave();
    if (action === 'mute') this.toggleMute();
    if (action === 'new-match') this.handleNewMatchTap();
    if (action === 'toggle-theme') this.toggleTheme();
    if (action === 'home') this.leave();
    if (action === 'confirm-age') this.confirmAge();
    if (action === 'decline-age') this.declineAge();
    if (action === 'open-report') this.openReport();
    if (action === 'close-report') this.closeReport();
    if (action === 'pick-reason') this.pickReason(actionEl.dataset.reason);
    if (action === 'submit-report') this.submitReport();
  }

  onInput(event) {
    if (!event.target.matches?.('[data-report-note]')) return;
    this.reportNote = event.target.value.slice(0, REPORT_NOTE_MAX);
  }

  confirmAge() {
    this.storage.setItem(AGE_KEY, '1');
    this.showAgeGate = false;
    return this.joinQueue();
  }

  declineAge() {
    this.showAgeGate = false;
    this.render();
  }

  openReport() {
    this.reportOpen = true;
    this.reportReason = '';
    this.reportNote = '';
    this.syncView();
  }

  closeReport() {
    this.reportOpen = false;
    this.reportReason = '';
    this.reportNote = '';
    this.syncView();
  }

  pickReason(reason) {
    this.reportReason = reason || '';
    this.root.querySelectorAll('[data-reason]').forEach((chip) => {
      chip.classList.toggle('selected', chip.dataset.reason === this.reportReason);
    });
    const submit = this.root.querySelector('[data-action="submit-report"]');
    if (submit) submit.disabled = !this.reportReason;
  }

  submitReport() {
    if (!this.reportReason) return;
    const note = this.reportNote.trim().slice(0, REPORT_NOTE_MAX);
    if (this.signaling.report) {
      this.signaling.report(this.reportReason, note);
    }
    this.reportOpen = false;
    this.reportReason = '';
    this.reportNote = '';
    this.leave();
    this.showError("Thanks. You won't be matched with them again.");
  }

  getClientKey() {
    let key = this.storage.getItem(CLIENT_KEY);
    if (!key) {
      key = this.idFactory();
      this.storage.setItem(CLIENT_KEY, key);
    }
    return key;
  }

  async joinQueue() {
    if (this.storage.getItem(AGE_KEY) !== '1') {
      this.showAgeGate = true;
      this.render();
      return;
    }

    try {
      this.leaving = false;
      this.mediaLive = false;
      this.sessionId = this.idFactory();
      this.setSessionLock();
      this.state = 'QUEUED';
      this.screen = 'matching';
      this.matchPhase = 'searching';
      this.callStartedAt = null;
      this.callSeconds = 0;
      this.render();

      const micPromise = this.webrtc.initLocalStream();
      const icePromise = this.webrtc.getIceConfig();
      const ready = await this.finishVoiceSetup(micPromise, icePromise);
      if (!ready || this.leaving) return;

      this.startSearchTimer();
      await this.ensureSignaling();
      if (this.leaving || this.screen !== 'matching') return;
      this.signaling.joinQueue(this.getClientKey());
    } catch (error) {
      console.error('Failed to join queue:', error);
      if (isIceConfigError(error)) {
        this.showVoiceSetupError();
        return;
      }
      this.resetToHome();
      this.showError('Failed to connect. Please try again.');
    }
  }

  async finishVoiceSetup(micPromise, icePromise) {
    const [micResult, iceResult] = await Promise.allSettled([micPromise, icePromise]);
    if (this.leaving || this.screen === 'home') return false;

    if (micResult.status === 'rejected') {
      console.error('Failed to start audio:', micResult.reason);
      this.leave();
      this.showError('Failed to start audio. Please check microphone permissions.');
      return false;
    }

    if (iceResult.status === 'rejected') {
      console.error('Failed to load ICE config:', iceResult.reason);
      this.showVoiceSetupError();
      return false;
    }

    return true;
  }

  showVoiceSetupError() {
    if (this.leaving) return;
    this.clearSearchTimer();
    this.clearMatchingTimers();
    this.clearCallTimers();
    this.stopRemoteAudio();
    this.mediaLive = false;
    this.matchPhase = 'searching';
    if (this.signaling.isOpen?.() || this.signaling.ws) {
      this.releaseOwnPresence();
      this.signaling.leave();
    }
    this.webrtc.close?.({ keepLocal: true });
    this.state = 'IDLE';
    this.screen = 'voice-setup';
    this.peerSessionId = null;
    this.currentMatch = null;
    this.render();
  }

  async ensureSignaling() {
    if (this.signaling.isOpen?.() || (this.signaling.ws && this.signaling.ws.readyState === 1)) {
      this.signaling.sessionId = this.sessionId;
      return;
    }
    await this.signaling.connect(this.sessionId);
  }

  async handleMatchFound(message) {
    this.clearSearchTimer();
    this.clearError();
    this.peerSessionId = message.peerSessionId;
    this.currentMatch = { peerSessionId: message.peerSessionId };
    this.state = 'MATCHED';
    this.matchPhase = 'found';
    this.screen = 'matching';
    this.mediaLive = false;
    this.callStartedAt = null;
    this.callSeconds = 0;
    this.clearCallTimers();
    this.render();

    try {
      await this.webrtc.initLocalStream();
      this.applyMuteStateToStream();

      if (message.initiator) {
        const offer = await this.webrtc.createOffer();
        this.signaling.sendOffer(offer);
      }
    } catch (error) {
      console.error('Failed to start call:', error);
      if (isIceConfigError(error)) {
        this.showVoiceSetupError();
        return;
      }
      this.showError('Failed to start audio. Please check microphone permissions.');
      this.leave();
    }
  }

  async handleOffer(message) {
    try {
      await this.webrtc.initLocalStream();
      this.applyMuteStateToStream();
      const answer = await this.webrtc.handleOffer(message.data);
      this.signaling.sendAnswer(answer);
      this.state = 'MATCHED';
    } catch (error) {
      console.error('Failed to handle offer:', error);
      if (isIceConfigError(error)) {
        this.showVoiceSetupError();
        return;
      }
      this.showError('Failed to connect. Please try again.');
      this.leave();
    }
  }

  async handleAnswer(message) {
    try {
      await this.webrtc.handleAnswer(message.data);
      this.state = 'MATCHED';
    } catch (error) {
      console.error('Failed to handle answer:', error);
    }
  }

  handleConnectionState(state) {
    if (state === 'connected') this.markCallLive();
  }

  markCallLive() {
    if (this.leaving) return;
    if (this.state !== 'MATCHED' && this.state !== 'CONNECTED') return;
    if (this.mediaLive && this.screen === 'active') return;
    this.mediaLive = true;
    this.enterCallUI();
  }

  handleIceCandidate(message) {
    this.webrtc.addIceCandidate(message.data);
  }

  handlePeerLeft() {
    this.returnToQueue('Your match left. Finding someone new.', { rejoin: false });
  }

  handlePeerReconnecting() {
    this.peerReconnectingAt = this.clock.now();
    this.state = 'RECONNECTING';
    this.stopRemoteAudio();
    this.timers.clearTimeout(this.reconnectToastTimer);
    this.reconnectToastTimer = this.timers.setTimeout(() => {
      if (this.state === 'RECONNECTING') {
        this.showError('Trying to reconnect...');
      }
    }, 1200);
  }

  async handleSessionResume() {
    const wasReconnecting = this.state === 'RECONNECTING';
    const now = this.clock.now();
    const inGracePeriod = this.peerReconnectingAt ? now - this.peerReconnectingAt < this.reconnectGracePeriodMs : false;

    if (this.reconnectFallbackTimer) {
      this.timers.clearTimeout(this.reconnectFallbackTimer);
      this.reconnectFallbackTimer = null;
    }
    this.timers.clearTimeout(this.reconnectToastTimer);
    this.reconnectToastTimer = null;
    if (this.errorMessage === 'Trying to reconnect...' || this.errorMessage === 'Connection lost. Attempting to reconnect...') {
      this.errorMessage = '';
      this.root.querySelector('.error-toast')?.remove();
    }

    this.storage.removeItem(PREV_SESSION_KEY);

    try {
      if (!this.webrtc.pc) {
        await this.webrtc.initLocalStream();
        this.applyMuteStateToStream();
        this.webrtc.addLocalTracks();
        const offer = await this.webrtc.createOffer();
        this.signaling.sendOffer(offer);
        this.state = 'MATCHED';
      } else {
        await this.webrtc.restartIce();
        this.state = 'CONNECTED';
        if (!wasReconnecting || !inGracePeriod) this.markCallLive();
      }
    } catch (error) {
      if (isIceConfigError(error)) {
        this.showVoiceSetupError();
        return;
      }
      this.showError('Failed to reconnect. Please try again.');
      this.leave();
    }
  }

  handleTimeout() {
    this.returnToQueue('Peer did not reconnect. Finding a new match.', { rejoin: false });
  }

  handleDisconnect() {
    if (this.leaving || this.screen === 'home') return;
    this.restoreSignaling();
  }

  async restoreSignaling() {
    if (this.leaving || this.screen === 'home') return;
    if (this.signaling.isOpen?.()) return;
    if (this.signalingReconnectTimer) return;

    this.signalingReconnectTimer = this.timers.setTimeout(async () => {
      this.signalingReconnectTimer = null;
      if (this.leaving || this.screen === 'home' || this.signaling.isOpen?.()) return;
      try {
        await this.signaling.connect(this.sessionId);
        if (this.state === 'MATCHED' || this.state === 'CONNECTED' || this.state === 'RECONNECTING') {
          this.saveSessionForReconnect();
          this.signaling.reconnect();
        } else if (this.state === 'QUEUED' || this.screen === 'empty' || this.screen === 'matching') {
          this.signaling.joinQueue(this.getClientKey());
        }
      } catch (error) {
        console.error('Signaling reconnect failed:', error);
        this.showError('Connection lost. Attempting to reconnect...');
        this.timers.setTimeout(() => this.restoreSignaling(), 1000);
      }
    }, 250);
  }

  returnToQueue(message, { rejoin = false } = {}) {
    this.clearMatchingTimers();
    this.clearCallTimers();
    this.stopRemoteAudio();
    this.webrtc.close({ keepLocal: true });
    this.resetMute(false);
    this.mediaLive = false;
    this.callStartedAt = null;
    this.callSeconds = 0;
    this.skipArmed = false;
    this.peerSessionId = null;
    this.currentMatch = null;
    this.state = 'QUEUED';
    this.screen = 'matching';
    this.matchPhase = 'searching';
    this.showError(message);
    this.render();
    this.startSearchTimer();
    if (rejoin) this.rejoinQueue();
  }

  rejoinQueue() {
    if (this.signaling.isOpen?.() || (this.signaling.ws && this.signaling.ws.readyState === 1)) {
      this.signaling.sessionId = this.sessionId;
      this.signaling.joinQueue(this.getClientKey());
      return;
    }
    this.joinQueue();
  }

  handleNewMatchTap() {
    if (!this.skipArmed) {
      this.skipArmed = true;
      this.render();
      this.timers.clearTimeout(this.skipResetTimer);
      this.skipResetTimer = this.timers.setTimeout(() => {
        this.skipArmed = false;
        this.render();
      }, 2000);
      return;
    }

    this.timers.clearTimeout(this.skipResetTimer);
    this.skipArmed = false;
    this.render();
    this.timers.setTimeout(() => this.findNewMatch(), 300);
  }

  async findNewMatch() {
    this.webrtc.close({ keepLocal: true });
    this.stopRemoteAudio();
    // Mute policy: a new match unmutes. Same as the previous product —
    // skip starts a new call with the microphone on.
    this.resetMute(false);
    this.callStartedAt = null;
    this.callSeconds = 0;
    this.clearCallTimers();
    this.clearAllSessionStorage();
    this.peerSessionId = null;
    this.currentMatch = null;
    this.state = 'QUEUED';
    this.screen = 'matching';
    this.matchPhase = 'searching';
    this.render();
    this.startSearchTimer();

    if (this.signaling.ws) {
      this.signaling.leaveSession();
      return;
    }
    await this.joinQueue();
  }

  leave() {
    this.leaving = true;
    this.releaseOwnPresence();
    this.clearSearchTimer();
    this.clearMatchingTimers();
    this.clearCallTimers();
    this.mediaLive = false;
    if (this.state === 'RECONNECTING') {
      this.signaling.cancelReconnect();
    } else {
      this.signaling.leave();
    }
    this.webrtc.close();
    this.stopRemoteAudio();
    this.resetToHome();
    this.leaving = false;
  }

  enterCallUI() {
    this.clearSearchTimer();
    this.clearMatchingTimers();
    const alreadyLive = this.screen === 'active' && this.callStartedAt;
    this.state = 'CONNECTED';
    this.screen = 'active';
    this.matchPhase = 'found';
    this.mediaLive = true;
    if (!alreadyLive) {
      this.callStartedAt = this.clock.now();
      this.callSeconds = 0;
      this.startCallTimer();
    }
    this.render();
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    this.applyMuteStateToStream();
    if (this.isMuted) this.isSpeaking = false;

    const button = this.root.querySelector('[data-action="mute"]');
    if (!button) {
      this.render();
      return;
    }

    button.classList.toggle('muted', this.isMuted);
    const icon = button.querySelector('.control-icon');
    const label = button.querySelector('[data-mute-label]');
    if (icon) icon.textContent = this.isMuted ? '🔇' : '🎙';
    if (label) label.textContent = this.isMuted ? 'Unmute' : 'Mute';
    this.updateActiveMotion();
  }

  toggleTheme() {
    this.isDark = !this.isDark;
    this.render();
  }

  startCallTimer() {
    this.timers.clearInterval(this.callTimer);
    this.callTimer = this.timers.setInterval(() => {
      if (!this.callStartedAt || this.screen !== 'active') return;
      this.callSeconds = Math.floor((this.clock.now() - this.callStartedAt) / 1000);
      this.updateActiveMotion();
    }, 1000);
  }

  updateActiveMotion() {
    if (this.screen !== 'active') return;

    const speaking = this.isSpeaking && !this.isMuted;
    const orb = this.root.querySelector('.orb-wrap');
    const bars = this.root.querySelector('.wave-bars');

    if (orb) orb.classList.toggle('speaking', speaking);
    if (bars) bars.classList.toggle('active', speaking);
    this.root.querySelectorAll('.timer-chip').forEach((timer) => {
      timer.textContent = this.formatTime(this.callSeconds);
    });
  }

  async tryReconnect() {
    const savedSessionId = this.storage.getItem(PREV_SESSION_KEY);
    if (!savedSessionId) return false;

    try {
      this.sessionId = savedSessionId;
      this.setSessionLock();
      this.state = 'RECONNECTING';
      this.screen = 'matching';
      this.matchPhase = 'found';
      this.render();

      await this.signaling.connect(this.sessionId);
      this.signaling.reconnect();

      this.reconnectFallbackTimer = this.timers.setTimeout(() => {
        if (this.state !== 'MATCHED' && this.state !== 'CONNECTED') {
          this.resetToHome();
        }
      }, 10000);

      return true;
    } catch (error) {
      console.error('[Reconnect] Failed:', error);
      this.resetToHome();
      return false;
    }
  }

  saveSessionForReconnect() {
    if (this.sessionId && (this.state === 'MATCHED' || this.state === 'CONNECTED' || this.state === 'RECONNECTING')) {
      this.storage.setItem(PREV_SESSION_KEY, this.sessionId);
    }
  }

  setSessionLock() {
    this.storage.setItem(STORAGE_KEY, this.sessionId);
  }

  clearAllSessionStorage() {
    this.storage.removeItem(STORAGE_KEY);
    this.storage.removeItem(PREV_SESSION_KEY);
  }

  resetToHome() {
    this.clearSearchTimer();
    this.clearMatchingTimers();
    this.clearCallTimers();
    this.showAgeGate = false;
    this.reportOpen = false;
    this.reportReason = '';
    this.reportNote = '';
    this.clearAllSessionStorage();
    this.resetMute(false);
    this.state = 'IDLE';
    this.screen = 'home';
    this.peerSessionId = null;
    this.currentMatch = null;
    this.callStartedAt = null;
    this.callSeconds = 0;
    this.isSpeaking = false;
    this.skipArmed = false;
    this.mediaLive = false;
    this.errorMessage = '';
    this.render();
  }

  resetMute(render = true) {
    this.isMuted = false;
    if (render) this.render();
  }

  clearMatchingTimers() {
    this.timers.clearTimeout(this.matchEnterTimer);
    this.matchEnterTimer = null;
    this.clearSearchTimer();
  }

  startSearchTimer() {
    this.clearSearchTimer();
    this.searchGeneration += 1;
    const generation = this.searchGeneration;
    this.searchTimer = this.timers.setTimeout(() => {
      if (generation !== this.searchGeneration) return;
      this.enterEmptyRoom();
    }, this.searchTimeoutMs);
  }

  clearSearchTimer() {
    this.timers.clearTimeout(this.searchTimer);
    this.searchTimer = null;
    this.searchGeneration += 1;
  }

  enterEmptyRoom() {
    if (this.leaving) return;
    if (this.state !== 'QUEUED' || this.screen !== 'matching') return;
    if (this.matchPhase === 'found') return;

    if (!this.presenceReady || this.getPresence().others > 0) {
      this.startSearchTimer();
      return;
    }

    this.clearSearchTimer();
    this.state = 'QUEUED';
    this.screen = 'empty';
    this.matchPhase = 'searching';
    this.mediaLive = false;
    this.render();
  }

  startPresencePolling() {
    this.refreshPresence();
    this.timers.clearInterval(this.presenceTimer);
    this.presenceTimer = this.timers.setInterval(() => this.refreshPresence(), PRESENCE_POLL_MS);
  }

  async refreshPresence() {
    const epoch = this.presenceEpoch;
    try {
      const count = await this.presence.getCount();
      if (epoch !== this.presenceEpoch) return;
      if (!Number.isFinite(count)) return;
      const socketOpen = this.isCountedInPresence();
      if (this.dropOneSelfFromPresence && !socketOpen) {
        this.dropOneSelfFromPresence = false;
        this.updateActiveUsers(Math.max(0, count - 1), { includesSelf: false });
        return;
      }
      if (socketOpen) this.dropOneSelfFromPresence = false;
      this.updateActiveUsers(count, { includesSelf: socketOpen });
    } catch (error) {
      if (epoch !== this.presenceEpoch) return;
      console.warn('Failed to refresh presence:', error);
    }
  }

  isCountedInPresence() {
    return Boolean(this.signaling.isOpen?.() || (this.signaling.ws && this.signaling.ws.readyState === 1));
  }

  applySocketPresence(count) {
    if (!this.isCountedInPresence()) return;
    this.dropOneSelfFromPresence = false;
    this.updateActiveUsers(count, { includesSelf: true });
  }

  releaseOwnPresence() {
    const counted = this.presenceIncludesSelf || this.isCountedInPresence();
    if (!counted) return;
    this.dropOneSelfFromPresence = true;
    this.presenceEpoch += 1;
    if (Number.isFinite(this.activeUsers)) {
      this.updateActiveUsers(Math.max(0, this.activeUsers - 1), { includesSelf: false });
    } else {
      this.presenceIncludesSelf = false;
    }
    this.refreshPresence();
  }

  getPresence() {
    const count = Number.isFinite(this.activeUsers) ? this.activeUsers : 0;
    const includesSelf = Boolean(this.presenceIncludesSelf) && count > 0;
    const others = includesSelf ? Math.max(0, count - 1) : count;

    if (others <= 0) {
      return includesSelf
        ? { kind: 'solo', others: 0, text: "You're the only one here" }
        : { kind: 'empty', others: 0, text: "Nobody's here yet" };
    }
    if (others === 1) {
      return { kind: 'few', others: 1, text: '1 person here' };
    }
    return {
      kind: 'crowd',
      others,
      text: `${this.formatCount(others)} online right now`,
    };
  }

  clearCallTimers() {
    this.timers.clearInterval(this.callTimer);
    this.timers.clearTimeout(this.speakingTimer);
    this.callTimer = null;
    this.speakingTimer = null;
  }

  applyMuteStateToStream() {
    if (!this.webrtc.localStream) return;
    this.webrtc.localStream.getAudioTracks().forEach((track) => {
      track.enabled = !this.isMuted;
    });
  }

  stopRemoteAudio() {
    const audio = document.getElementById('remote-audio');
    if (audio) {
      audio.pause();
      audio.srcObject = null;
    }
  }

  playRemoteAudio(stream) {
    let audio = document.getElementById('remote-audio');
    if (!audio) {
      audio = document.createElement('audio');
      audio.id = 'remote-audio';
      audio.autoplay = true;
      audio.playsInline = true;
      audio.style.display = 'none';
      document.body.appendChild(audio);
    }

    audio.srcObject = stream;
    audio.play().catch(() => {
      this.showError('Tap the page once to hear your match.');
      const startAudio = () => {
        audio.play().catch(() => {});
        document.removeEventListener('click', startAudio);
        document.removeEventListener('touchstart', startAudio);
      };
      document.addEventListener('click', startAudio);
      document.addEventListener('touchstart', startAudio);
    });
  }

  updateActiveUsers(count, { includesSelf } = {}) {
    const next = Number.isFinite(count) ? count : null;
    if (next === null) return;
    const kindBefore = this.getPresence().kind;
    const othersBefore = this.getPresence().others;
    const wasReady = this.presenceReady;
    this.activeUsers = next;
    this.presenceIncludesSelf = includesSelf ?? this.isCountedInPresence();
    this.presenceReady = true;
    const kindAfter = this.getPresence().kind;
    const othersAfter = this.getPresence().others;

    if (!wasReady || kindBefore !== kindAfter || othersBefore !== othersAfter) {
      this.syncView();
      return;
    }

    this.syncPresence();
  }

  showError(message) {
    this.errorMessage = message;
    let toast = this.root.querySelector('.error-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'error-toast';
      this.root.appendChild(toast);
    }
    toast.textContent = message;
    this.timers.clearTimeout(this.errorTimer);
    this.errorTimer = this.timers.setTimeout(() => {
      this.clearError();
    }, 5000);
  }

  clearError() {
    this.errorMessage = '';
    this.timers.clearTimeout(this.errorTimer);
    this.errorTimer = null;
    this.root.querySelector('.error-toast')?.remove();
  }

  liveHoursLine() {
    return getLiveStatus(new Date(this.clock.now())).line;
  }

  getGreeting() {
    const hour = new Date(this.clock.now()).getHours();
    if (hour < 12) return 'Good morning.';
    if (hour < 18) return 'Good afternoon.';
    return 'Good evening.';
  }

  formatCount(value = this.activeUsers) {
    const count = Number.isFinite(value) ? value : 0;
    return new Intl.NumberFormat('en-US').format(count);
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }

  matchDots() {
    return '<span class="dot-cycle">...</span>';
  }

  isDesktop() {
    return window.matchMedia('(min-width: 768px)').matches;
  }

  escape(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }
}

export function bootstrap() {
  const app = new App();
  app.tryReconnect();
  return app;
}

if (!import.meta.env?.VITEST) {
  bootstrap();
}
